import { useCallback, useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { X } from "lucide-react";
import axios from "axios";
import confetti from "canvas-confetti";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// localStorage keys
const LS_SUBSCRIBED = "melbi_nl_subscribed";
const LS_DISMISSED_UNTIL = "melbi_nl_dismissed_until";

// 14-day cooldown after dismissal (ms)
const DISMISS_COOLDOWN_MS = 14 * 24 * 60 * 60 * 1000;

// 10-second delay before showing on landing
const OPEN_DELAY_MS = 10 * 1000;

// Any route prefix that should never show the popup.
// (The medical intake, checkout, and patient portal live on external domains
// so they're already excluded. This list is here for future internal routes
// that should be added.)
const EXCLUDED_ROUTE_PREFIXES = [];

function shouldBlock() {
  try {
    if (localStorage.getItem(LS_SUBSCRIBED) === "1") return true;
    const until = localStorage.getItem(LS_DISMISSED_UNTIL);
    if (until && Date.now() < Number(until)) return true;
  } catch {
    /* localStorage disabled — fail open (show popup) */
  }
  return false;
}

export default function NewsletterPopup({ forceOpen = false, onClose }) {
  const { pathname } = useLocation();
  const [open, setOpen] = useState(forceOpen);
  const [fullName, setFullName] = useState("");
  const [email, setEmail] = useState("");
  const [interest, setInterest] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [status, setStatus] = useState({ kind: "idle", message: "" });
  const shownThisSession = useRef(forceOpen);

  const routeExcluded = EXCLUDED_ROUTE_PREFIXES.some((p) => pathname.startsWith(p));

  const showIfEligible = useCallback(() => {
    if (shownThisSession.current) return;
    if (shouldBlock()) return;
    if (routeExcluded) return;
    shownThisSession.current = true;
    setOpen(true);
  }, [routeExcluded]);

  // Timer + exit-intent triggers (skipped entirely when forceOpen is true)
  useEffect(() => {
    if (forceOpen) return undefined;
    if (routeExcluded) return undefined;
    if (shouldBlock()) return undefined;

    const timerId = setTimeout(showIfEligible, OPEN_DELAY_MS);

    // Exit-intent on desktop only (viewport >= 768px)
    const isDesktop = typeof window !== "undefined" && window.matchMedia("(min-width: 768px)").matches;
    const onMouseLeave = (e) => {
      if (e.clientY <= 0) showIfEligible();
    };
    if (isDesktop) {
      document.addEventListener("mouseleave", onMouseLeave);
    }

    return () => {
      clearTimeout(timerId);
      if (isDesktop) document.removeEventListener("mouseleave", onMouseLeave);
    };
  }, [forceOpen, routeExcluded, showIfEligible]);

  const close = useCallback(() => {
    setOpen(false);
    if (!forceOpen) {
      try {
        localStorage.setItem(LS_DISMISSED_UNTIL, String(Date.now() + DISMISS_COOLDOWN_MS));
      } catch {
        /* ignore */
      }
    }
    if (typeof onClose === "function") onClose();
  }, [forceOpen, onClose]);

  // Escape to close
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") close();
    };
    document.addEventListener("keydown", onKey);
    // Lock body scroll while open
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [open, close]);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    setStatus({ kind: "idle", message: "" });
    try {
      const res = await axios.post(`${API}/waitlist`, {
        first_name: fullName.trim(),
        email: email.trim(),
        interest: interest,
        source: "newsletter-popup",
      });
      const data = res?.data || {};
      if (data.status === "ok" || data.status === "saved") {
        try {
          localStorage.setItem(LS_SUBSCRIBED, "1");
          localStorage.removeItem(LS_DISMISSED_UNTIL);
        } catch {
          /* ignore */
        }
        setStatus({
          kind: "success",
          message: "Your 10% welcome code is on its way to your inbox — check for an email from us in the next couple of minutes.",
        });
        if (typeof window !== "undefined" && typeof window.fbq === "function") {
          window.fbq("track", "Lead", { content_name: "Newsletter Popup 10% Off" });
        }
      } else {
        setStatus({ kind: "error", message: "Something went wrong. Please try again." });
      }
    } catch (err) {
      setStatus({
        kind: "error",
        message: "We couldn't complete your signup right now. Please try again in a moment.",
      });
    } finally {
      setSubmitting(false);
    }
  };

  // Brand-colored confetti on the confirmation screen. Two staggered bursts
  // from the bottom corners so the animation lands under the message area
  // rather than covering the copy the user is trying to read.
  useEffect(() => {
    if (status.kind !== "success") return undefined;
    const brandColors = ["#C96A4E", "#B35A40", "#8A9A92", "#F5E6D3", "#FAF9F6"];
    const fire = (originX) => {
      confetti({
        particleCount: 60,
        angle: originX < 0.5 ? 60 : 120,
        spread: 55,
        startVelocity: 45,
        gravity: 0.9,
        ticks: 140,
        origin: { x: originX, y: 0.9 },
        colors: brandColors,
        scalar: 0.9,
        disableForReducedMotion: true,
        zIndex: 200,
      });
    };
    fire(0.15);
    const t = setTimeout(() => fire(0.85), 180);
    return () => clearTimeout(t);
  }, [status.kind]);

  if (!open) return null;

  const isSuccess = status.kind === "success";
  const formValid = fullName.trim() && email.trim() && interest;

  return (
    <div
      data-testid="newsletter-popup-backdrop"
      className="fixed inset-0 z-[100] flex items-center justify-center px-4 py-6 bg-[#2C2B29]/55 backdrop-blur-[2px]"
      role="dialog"
      aria-modal="true"
      aria-labelledby="newsletter-popup-title"
      onMouseDown={(e) => {
        // Clicking the backdrop (not the modal) counts as dismiss
        if (e.target === e.currentTarget) close();
      }}
    >
      <div
        data-testid="newsletter-popup"
        className="relative w-full max-w-md bg-[#FAF9F6] rounded-3xl shadow-2xl border border-[#E8E5DF] p-6 sm:p-8"
      >
        <button
          type="button"
          onClick={close}
          aria-label="Close"
          data-testid="newsletter-popup-close"
          className="absolute top-3 right-3 inline-flex items-center justify-center h-9 w-9 rounded-full text-[#5E5C58] hover:text-[#2C2B29] hover:bg-[#E8E5DF]/50 transition-colors"
        >
          <X size={18} strokeWidth={2} />
        </button>

        {isSuccess ? (
          <div data-testid="newsletter-popup-success" className="text-center pt-4 pb-2">
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#C96A4E]">
              You&apos;re in — Welcome to Melbi
            </p>
            <h2
              id="newsletter-popup-title"
              className="mt-3 font-display text-2xl sm:text-3xl font-semibold leading-tight text-[#2C2B29]"
            >
              Your 10% Off is on the way!
            </h2>
            <p className="mt-4 text-[#2C2B29] leading-relaxed text-sm sm:text-base">
              {status.message}
            </p>
            <button
              type="button"
              onClick={close}
              data-testid="newsletter-popup-success-close"
              className="mt-6 inline-flex items-center justify-center bg-[#C96A4E] text-white rounded-full px-6 py-3 text-sm font-semibold hover:bg-[#B35A40] active:scale-[0.98] transition-all shadow-md"
            >
              Close
            </button>
          </div>
        ) : (
          <>
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#C96A4E]">
              Exclusive offer
            </p>
            <h2
              id="newsletter-popup-title"
              className="mt-2 font-display text-2xl sm:text-3xl font-semibold leading-tight text-[#2C2B29]"
            >
              Stay Connected with Melbi
            </h2>
            <p className="mt-3 text-[#2C2B29] leading-relaxed text-sm sm:text-base">
              Be the first to know when we launch new wellness offerings and receive exclusive promotions. Sign up today and receive <span className="text-[#C96A4E] font-semibold">10% off</span> your first eligible order.
            </p>

            <form onSubmit={onSubmit} className="mt-5 space-y-3" noValidate>
              <div>
                <label
                  htmlFor="newsletter-popup-full-name"
                  className="block text-xs font-semibold uppercase tracking-[0.14em] text-[#5E5C58] mb-1.5"
                >
                  Full Name
                </label>
                <input
                  id="newsletter-popup-full-name"
                  data-testid="newsletter-popup-full-name"
                  type="text"
                  required
                  autoComplete="name"
                  value={fullName}
                  onChange={(e) => setFullName(e.target.value)}
                  className="w-full bg-white border border-[#E8E5DF] rounded-2xl px-4 py-3 text-sm text-[#2C2B29] placeholder:text-[#8A9A92] focus:outline-none focus:border-[#C96A4E] focus:ring-2 focus:ring-[#C96A4E]/20 transition-colors"
                  placeholder="Jane Doe"
                />
              </div>
              <div>
                <label
                  htmlFor="newsletter-popup-email"
                  className="block text-xs font-semibold uppercase tracking-[0.14em] text-[#5E5C58] mb-1.5"
                >
                  Email Address
                </label>
                <input
                  id="newsletter-popup-email"
                  data-testid="newsletter-popup-email"
                  type="email"
                  required
                  autoComplete="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full bg-white border border-[#E8E5DF] rounded-2xl px-4 py-3 text-sm text-[#2C2B29] placeholder:text-[#8A9A92] focus:outline-none focus:border-[#C96A4E] focus:ring-2 focus:ring-[#C96A4E]/20 transition-colors"
                  placeholder="you@example.com"
                />
              </div>
              <div>
                <label
                  htmlFor="newsletter-popup-interest"
                  className="block text-xs font-semibold uppercase tracking-[0.14em] text-[#5E5C58] mb-1.5"
                >
                  What are you most interested in?
                </label>
                <select
                  id="newsletter-popup-interest"
                  data-testid="newsletter-popup-interest"
                  required
                  value={interest}
                  onChange={(e) => setInterest(e.target.value)}
                  className="w-full bg-white border border-[#E8E5DF] rounded-2xl px-4 py-3 text-sm text-[#2C2B29] focus:outline-none focus:border-[#C96A4E] focus:ring-2 focus:ring-[#C96A4E]/20 transition-colors appearance-none"
                >
                  <option value="" disabled>Select one…</option>
                  <option value="Recovery">Recovery</option>
                  <option value="Weight Management">Weight Management</option>
                  <option value="Vitality">Vitality</option>
                  <option value="Energy">Energy</option>
                  <option value="Antioxidant Support">Antioxidant Support</option>
                  <option value="Metabolic Support">Metabolic Support</option>
                  <option value="Skin Care">Skin Care</option>
                  <option value="General Wellness / Not Sure Yet">General Wellness / Not Sure Yet</option>
                </select>
              </div>

              {status.kind === "error" && (
                <p
                  data-testid="newsletter-popup-error"
                  role="alert"
                  className="text-sm text-[#C96A4E]"
                >
                  {status.message}
                </p>
              )}

              <button
                type="submit"
                disabled={submitting || !formValid}
                data-testid="newsletter-popup-submit"
                className={`mt-2 w-full inline-flex items-center justify-center rounded-full px-6 py-3.5 text-base font-semibold text-white transition-all duration-200 ${
                  submitting
                    ? "bg-[#B35A40] cursor-wait"
                    : !formValid
                    ? "bg-[#C96A4E]/70 cursor-not-allowed"
                    : "bg-[#C96A4E] hover:bg-[#B35A40] shadow-lg shadow-[#C96A4E]/30 hover:shadow-xl hover:shadow-[#C96A4E]/40 hover:-translate-y-0.5 active:translate-y-0 active:scale-[0.98] ring-2 ring-[#B35A40]/25"
                }`}
              >
                {submitting ? "Signing you up…" : "Get 10% Off"}
              </button>
            </form>

            <p className="mt-4 text-[11px] text-[#5E5C58] leading-relaxed">
              By signing up, you agree to receive marketing emails from Melbi Wellness. You may unsubscribe at any time.
            </p>
          </>
        )}
      </div>
    </div>
  );
}
